/**
 * Is a person using this tab? (ADR 0082)
 *
 * The server slides a session's idle expiry on any authenticated request a
 * person made, and only those. A mail client sends plenty of requests nobody
 * made — the new-mail poll, reminder checks, background refetches — and if
 * those slid the session, a signed-in tab left open on a shared machine would
 * never reach its idle timeout. So the transport marks every request sent while
 * this tab looks unattended as passive (`X-Session-Passive: 1`), and the server
 * records it as "last seen" without extending anything.
 *
 * "Attended" is judged from input, not from requests: a pointer, key, wheel or
 * touch event within {@link ACTIVE_WINDOW_MS}, in a tab that is visible. The
 * same record answers the session provider's other question — "has this person
 * done anything since the server last extended the session?" — which is what
 * keeps the expiry banner away from someone typing a long message that sends no
 * requests at all.
 *
 * Plain TypeScript, no React: the transport imports it.
 */

/** Input within this long, in a visible tab, counts as a person being there. */
export const ACTIVE_WINDOW_MS = 60_000;

/** The request header that tells the server "nobody made this one". */
export const SESSION_PASSIVE_HEADER = 'X-Session-Passive';

/**
 * The input that counts. `pointermove` is included on purpose: a hand on the
 * mouse is presence even when nothing is clicked. It is cheap — the handler
 * stores a number.
 */
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Starts at module load: opening the app is itself something a person did, so
 * the first requests after a cold start are not passive.
 */
let lastInputAt = Date.now();
let installed = false;

function onInput(): void {
  lastInputAt = Date.now();
}

/** Listens for input on the window. Idempotent; a no-op outside a browser. */
export function startActivityTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const type of INPUT_EVENTS) {
    // Capture: a handler that stops propagation (a menu, the compose editor)
    // must not make its user look idle.
    window.addEventListener(type, onInput, { capture: true, passive: true });
  }
}

/** When this tab last saw input, in epoch millis. */
export function lastUserActivity(): number {
  return lastInputAt;
}

/** Records input explicitly — tests, and anything that is activity without an event. */
export function noteUserActivity(at: number = Date.now()): void {
  if (at > lastInputAt) lastInputAt = at;
}

/** Test seam: set the last-input time outright, backwards included. */
export function resetUserActivity(at: number = Date.now()): void {
  lastInputAt = at;
}

function tabVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** Whether a request sent now was plausibly made by a person. */
export function isUserActive(now: number = Date.now()): boolean {
  return tabVisible() && now - lastInputAt < ACTIVE_WINDOW_MS;
}

startActivityTracking();
